// Invite-claim capture — the referee half of the referral link.
//
// WHERE THIS SITS. `/r/<code>` (a server route handler) POSTs the claim to Vapor,
// gets back an opaque claimToken, and redirects to /welcome?ic=<token>. The search
// calls in ticketCalls.jsx forward it as `inviteClaimToken`, which is the field
// InviteAttributionService reads.
//
// ── THE TOKEN LIVES IN THE URL. NOTHING IS STORED. ──────────────────────────
// It used to ride sessionStorage, written once on first paint and FIRST-WRITE-WINS.
// That was wrong, and it misattributed five real drivers on 2026-10-09: a token
// stashed at 00:09 was still being sent at 04:23, so one tap on one referral link
// minted FIVE Invites to the same referrer, and a driver who later followed a
// DIFFERENT friend's link was credited to the first one anyway. Nothing errored.
//
// The root cause was not where it was stored — a cookie or localStorage has the
// identical bug, and localStorage is worse because it outlives the tab. The root
// cause was STORING IT AT ALL: stored state outlives the visit it belongs to.
//
// So the URL is the carrier. The claim row already exists server-side the moment
// the link is tapped, so the client is only carrying a POINTER to a row the server
// already has, and it only has to survive until the user is minted. Each hop that
// leads to a search appends it the same way `gclid` is carried
// (MobileLandingPageScrollB / AboveTheFold).
//
// ⚠️ THIS FAILS CLOSED, AND THAT IS THE POINT. Lose the param on a back button or
// a detour and NOBODY is attributed — a missed referral, which is recoverable. The
// stored version failed OPEN: it paid the wrong person, silently, five times.
//
// ⚠️ IT IS ALSO LAST-TOUCH WITHIN A VISIT, a deliberate policy change. Tap friend
// A's link, then friend B's, then search → B is paid. The old behaviour locked A
// in forever. `UNIQUE(refereeUserID)` still means a referee is attributed at most
// once, and `UNIQUE(claimID)` on Invites (AddUniqueClaimIDToInvites) means one tap
// mints at most one Invite — those two are what make it 1:1, not the client.
//
// NOT SENSITIVE. The token identifies a tap on an invite code, never a person, and
// the code it refers to was already in the URL the visitor just followed. It now
// stays in the address bar for the visit rather than one paint, so it must be
// scrubbed at the analytics boundary rather than left to ride into every capture.

// Kept EXPORTED so a stale value left by the old sessionStorage implementation can
// be cleared from any browser that still holds one (see purgeLegacyStoredClaim).
export const INVITE_CLAIM_KEY = 'tcktpass_invite_claim'

// Short and opaque on purpose — it appears in the address bar for one paint.
export const INVITE_CLAIM_PARAM = 'ic'

// ── Did the code actually resolve to a referrer? ─────────────────────────────
// ⚠️ A CLAIM TOKEN IS NOT EVIDENCE OF A REFERRER. InviteController.recordClaim
// issues a token for EVERY tap, including unknown, mistyped and retired codes —
// deliberately, so a visitor is never 404'd for our bookkeeping. `resolved` is
// the separate fact that the code matched a real user.
//
// It rides the same one-hop redirect as the token and is stashed beside it, as a
// PAIR: both are written together or neither is, so nothing downstream can read
// a token with a stale resolved flag from an earlier tap.
//
// The only consumer today is the landing strip, which promises the visitor $5.
// That promise must never be shown on an unresolved claim: the link was genuine
// to the person who followed it, the money will simply never arrive, and that is
// a promise WE broke.
export const INVITE_CLAIM_RESOLVED_KEY = 'tcktpass_invite_claim_resolved'
export const INVITE_CLAIM_RESOLVED_PARAM = 'icr'

// ── The referee's credit, in cents ───────────────────────────────────────────
// ⚠️ MIRRORS InviteAttributionService.refereeGrantCents (Server/TapeAPI/Sources/
// App/Services/InviteAttributionService.swift). There is NO shared source of
// truth across the two languages, so these two numbers can silently diverge and
// the only symptom would be a landing page promising one figure while the ledger
// grants another. If you change one, change the other. A comment on the Swift
// side names this constant.
export const REFERRAL_REWARD_CENTS = 500

// Cents → "$5" / "$5.50". Whole dollars drop the ".00", matching how every other
// money figure in the app reads.
export function formatRewardCents(cents = REFERRAL_REWARD_CENTS) {
    const dollars = (Number(cents) || 0) / 100
    return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

// Reads the claim token OFF THE CURRENT URL, or null if this visit didn't come
// through a referral link. Never throws: attribution must never break the page.
//
// ⚠️ NO FALLBACK TO STORAGE. A fallback would resurrect the exact bug this
// replaces — a token from an earlier visit winning over the link just tapped.
// Absent means absent.
export function getInviteClaimToken() {
    if (typeof window === 'undefined') return null
    try {
        const v = new URLSearchParams(window.location.search).get(INVITE_CLAIM_PARAM)
        return (v || '').trim() || null
    } catch (error) {
        return null
    }
}

// True ONLY when this session's claim resolved to a real referrer. Anything else
// — no claim, unresolved claim, storage unavailable — is false, so every caller
// fails closed to "make no promise".
export function getInviteClaimResolved() {
    if (typeof window === 'undefined') return false
    try {
        const p = new URLSearchParams(window.location.search)
        // Both halves, together. A resolved flag with no token is a promise with
        // nothing behind it, so it must never read true on its own.
        return p.get(INVITE_CLAIM_RESOLVED_PARAM) === '1'
            && !!(p.get(INVITE_CLAIM_PARAM) || '').trim()
    } catch (error) {
        return false
    }
}

// ── Carry the claim across a navigation ─────────────────────────────────────
// Returns the `&ic=…&icr=…` suffix to append to a destination URL, or '' when
// this visit carries no claim. This is how the token survives the landing → ticket
// scroll hop, which is a hard document navigation; it is the same mechanism
// `gclid` and `matched` already use at those call sites.
//
// ⚠️ EVERY NAVIGATION THAT CAN LEAD TO A SEARCH MUST CALL THIS. The six search
// calls in ticketCalls.jsx mint users, and they read the token off whatever URL
// the page was loaded with. A hop that forgets this suffix doesn't misattribute —
// it attributes NOBODY, and the referrer is silently never paid.
export function inviteClaimURLSuffix() {
    const token = getInviteClaimToken()
    if (!token) return ''
    const resolved = getInviteClaimResolved()
    return `&${INVITE_CLAIM_PARAM}=${encodeURIComponent(token)}`
        + (resolved ? `&${INVITE_CLAIM_RESOLVED_PARAM}=1` : '')
}

// ── One-time cleanup of the OLD stored token ────────────────────────────────
// Browsers that ran the sessionStorage implementation may still hold a token (and
// its resolved flag) from an earlier tab session. Nothing reads those keys any
// more, but leaving them is how a future reader accidentally reintroduces the bug.
// Runs on every page load, costs two removeItem calls, and never throws.
//
// This REPLACES captureInviteClaim(), which used to move `?ic=` into storage with
// first-write-wins semantics. There is nothing left to capture: the URL is the
// carrier, and the params are stripped only once the claim has been spent.
export function purgeLegacyStoredClaim() {
    if (typeof window === 'undefined') return
    try {
        window.sessionStorage.removeItem(INVITE_CLAIM_KEY)
        window.sessionStorage.removeItem(INVITE_CLAIM_RESOLVED_KEY)
    } catch (error) {
        // storage unavailable (Safari private mode) — nothing to purge
    }
}

// ── Spend it ────────────────────────────────────────────────────────────────
// Strips `ic` and `icr` from the address bar without a navigation, preserving
// every other param (?source=referral in particular, which POST /referrer reads
// as `context` and turns into channel="referral").
//
// Called AFTER a search has minted/attributed the user, not on first paint. That
// ordering is the whole design: stripping early is what forced the old code to
// stash the token somewhere, and the stash is what broke attribution. A token left
// in place one beat too long is harmless — the server refuses a second Invite on
// the same claim (UNIQUE(claimID)) — while one stripped too early is lost.
export function consumeInviteClaimFromURL() {
    if (typeof window === 'undefined') return
    try {
        const url = new URL(window.location.href)
        const had = [INVITE_CLAIM_PARAM, INVITE_CLAIM_RESOLVED_PARAM]
            .filter((p) => url.searchParams.has(p))
        if (had.length === 0) return
        had.forEach((p) => url.searchParams.delete(p))
        window.history.replaceState(
            window.history.state,
            '',
            `${url.pathname}${url.search}${url.hash}`
        )
    } catch (error) {
        // replaceState unavailable / exotic URL. The claim is already spent
        // server-side; a lingering param can only be refused, never double-paid.
    }
}
